import React, {MouseEvent, useCallback} from 'react';
import clsx from 'clsx';
import {Link, NavLink, useLocation, useNavigate} from 'react-router-dom';
import {Group, Burger, Drawer, Stack, Box, Text, rem, Anchor} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {IconBrandInstagram} from '@tabler/icons-react';
import classes from './Header.module.scss';

// @ts-ignore
import Logo from '@/assets/images/logo.svg?react';
import {navLinks} from '../navLinks';

export const Header: React.FC = () => {
    const [opened, {toggle, close}] = useDisclosure(false);
    const {pathname, hash} = useLocation();
    const navigate = useNavigate();

    const drawerId = 'main-mobile-drawer';

    const onHashClick = useCallback(
        (targetHash: string) => (e: MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            const h = targetHash.startsWith('#') ? targetHash : `#${targetHash}`;

            const go = () => {

                navigate({pathname: '/', hash: h});
                setTimeout(() => {
                    const el = document.getElementById(h.slice(1));
                    if (el) el.scrollIntoView({behavior: 'smooth', block: 'start'});
                }, 0);
            };

            if (pathname !== '/') {
                navigate('/');
                setTimeout(go, 0);
            } else {
                go();
            }
            close();
        },
        [pathname, navigate, close]
    );


    const onNavClickMobile = useCallback(() => {
        if (opened) close();
    }, [opened, close]);

    // Отрисовка списка ссылок: и для desktop, и для mobile
    const renderNavList = (isMobile = false) => (
        <ul className={clsx(classes.navigation__list, isMobile ? classes.navigation__mobile : '')} role="menubar"
            aria-label="Main navigation">
            {navLinks.map(({to, label, type = 'route', exact, hash: linkHash}) => {
                const isHash = type === 'hash';
                const href = to;

                // active для hash-ссылок
                const active = isHash
                    ? pathname === '/' && hash === (linkHash ?? to.slice(to.indexOf('#')))
                    : exact
                        ? pathname === to
                        : pathname.startsWith(to);

                return (
                    <li className={classes.navigation__item} key={href} role="none">
                        {isHash ? (
                            <a
                                href={href}
                                className={clsx(classes.navigation__link, {[classes.isActive]: active})}
                                onClick={onHashClick(linkHash || href.slice(href.indexOf('#')))}
                                role="menuitem"
                            >
                                {label}
                            </a>
                        ) : (
                            <NavLink
                                to={href}
                                className={({isActive}) =>
                                    clsx(classes.navigation__link, {
                                        [classes.isActive]: isActive || active,
                                    })
                                }
                                onClick={isMobile ? onNavClickMobile : undefined}
                                role="menuitem"
                                aria-current={active ? 'page' : undefined}
                            >
                                {label}
                            </NavLink>
                        )}
                    </li>
                );
            })}
        </ul>
    );

    return (
        <Box className={classes.header__inner} p={{base: 8, sm: 10, md: 14, lg: 16}} component="header">
            <Group justify="space-between" wrap="wrap">
                {/* Левый блок: на мобилке во всю ширину */}
                <Group
                    gap="sm"
                    className={classes.header__left}
                    justify="space-between"
                    w={{base: '100%', md: 'auto'}}
                    flex={{base: '0 0 100%', md: '0 0 auto'}}
                >
                    <Link to="/" className={classes.header__logo} aria-label="Home">
                        <Logo className={classes.logo}/>
                    </Link>

                    <Burger
                        aria-label={opened ? 'Close menu' : 'Open menu'}
                        aria-controls={drawerId}
                        opened={opened}
                        onClick={toggle}
                        hiddenFrom="md"
                    />
                </Group>

                {/* Центр: навигация desktop */}
                <Group
                    className={clsx(classes.navigation, classes.header__center)}
                    visibleFrom="md"
                    aria-label="Main navigation group"
                    wrap="nowrap"
                >
                    {renderNavList(false)}
                </Group>

                {/* Правый блок: соцсети desktop */}
                <Group className={classes.header__right} visibleFrom="md">
                    <Anchor
                        className={classes.header__social}
                        href="https://www.instagram.com/ifbbama/"
                        target="_blank"
                        rel="noreferrer noopener"
                        aria-label="@ifbbama on Instagram"

                    >
                        <IconBrandInstagram size={18}/>
                        <span>ifbbama</span>
                    </Anchor>
                </Group>
            </Group>

            {/* Мобильное меню */}
            <Drawer
                id={drawerId}
                opened={opened}
                onClose={close}
                position="left"
                size="100%"
                padding="md"
                hiddenFrom="md"
                aria-label="Mobile navigation"
                overlayProps={{opacity: 0.55, blur: 2}}
                withinPortal
                lockScroll
                withCloseButton
            >
                <Stack gap="lg" component="nav" role="navigation" aria-label="Mobile navigation">
                    {renderNavList(true)}

                    <Text ta="center" mt={'xl'}>
                        <Anchor
                            className={classes.header__social}
                            href="https://www.instagram.com/ifbbama/"
                            target="_blank"
                            rel="noreferrer noopener"
                            aria-label="@ifbbama on Instagram"
                            onClick={onNavClickMobile}
                            fs={rem(21)}
                        >
                            <IconBrandInstagram size={18}/>
                            <span>ifbbama</span>
                        </Anchor>
                    </Text>
                </Stack>
            </Drawer>
        </Box>
    );
};
